import {
  Box,
  Button,
  Center,
  Container,
  Heading,
  Image,
  Link,
  SimpleGrid,
  Text
} from "@chakra-ui/react"
import { Link as RouterLink } from "react-router-dom"
import CallToAction from "../components/CallToAction"
import FAQ from "../components/FAQ"
import Footer from "../components/Footer"
import HowItWorks from "../components/HowItWorks"

const audienceCards = [
  {
    label: "Institutional Investors",
    tagline:
      "Pinpoint high-yield retrofit opportunities across large portfolios with clear, quantified ROI.",
    to: "/institutional-investors"
  },
  {
    label: "Social Landlords",
    tagline:
      "Assess your entire portfolio, deploy your budget efficiently and unlock returns through smart tariffs.",
    to: "/social-landlords"
  },
  {
    label: "Energy Companies",
    tagline:
      "Identify the best homes for solar and heat pump deployment at scale.",
    to: "/energy-companies"
  }
]

const LandingPage: React.FC = () => (
  <Box>
    {/* Skip-to-form link: visible only on keyboard focus */}
    <Box
      as="a"
      href="#early-access"
      position="absolute"
      top="-60px"
      left={4}
      bg="brand.600"
      color="white"
      px={4}
      py={2}
      borderRadius="md"
      fontSize="sm"
      fontWeight="medium"
      zIndex={9999}
      _focus={{ top: 4 }}
      transition="top 0.15s ease"
    >
      Skip to sign-up form
    </Box>

    {/* Hero Section */}
    <Container maxW="container.xl" py={24} textAlign="center">
      <Heading
        as="h1"
        size="5xl"
        color="brand.600"
        mb={2}
        fontWeight={700}
        style={{ textWrap: "balance" } as React.CSSProperties}
      >
        Make smarter retrofit investments
      </Heading>
      <Text
        fontSize="xl"
        color="gray.600"
        mb={4}
        style={{ textWrap: "pretty" } as React.CSSProperties}
      >
        Prioritise your investments with data-driven insights in seconds, not
        weeks
      </Text>
      <Link href="#early-access">
        <Button
          bg="brand.600"
          color="white"
          size="lg"
          mb={6}
          _hover={{ bg: "brand.700" }}
        >
          Get Early Access
        </Button>
      </Link>
      <Center mt={8}>
        <Image
          src="/images/greenflip-product2.png"
          alt="GreenFlip platform showing portfolio ROI analysis"
          maxH="65vh"
          maxW="100%"
          objectFit="contain"
          borderRadius="lg"
        />
      </Center>

      {/* Built for your sector */}
      <Box mt={14}>
        <Text
          fontSize="sm"
          fontWeight="medium"
          mb={4}
          color="gray.600"
          textAlign="center"
        >
          Built for your sector
        </Text>
        <SimpleGrid columns={[1, 1, 3]} gap={4} maxW="900px" mx="auto">
          {audienceCards.map((card) => (
            <Link
              key={card.to}
              as={RouterLink}
              to={card.to}
              aria-label={`Learn more about GreenFlip for ${card.label}`}
              _hover={{ textDecoration: "none" }}
            >
              <Box
                p={5}
                borderRadius="xl"
                border="1px solid"
                borderColor="gray.200"
                textAlign="left"
                _hover={{
                  borderColor: "brand.500",
                  bg: "green.50",
                  shadow: "sm"
                }}
                transition="all 0.15s"
                h="full"
              >
                <Text
                  fontWeight="semibold"
                  color="brand.700"
                  fontSize="md"
                  mb={1}
                >
                  {card.label}
                </Text>
                <Text fontSize="sm" color="gray.600" lineHeight="tall" mb={3}>
                  {card.tagline}
                </Text>
                <Text color="brand.600" fontSize="sm" fontWeight="medium">
                  Learn more →
                </Text>
              </Box>
            </Link>
          ))}
        </SimpleGrid>
      </Box>
    </Container>

    {/* Supported By */}
    <Box bg="gray.100" py={10} px={12}>
      <Text
        fontSize="sm"
        fontWeight="medium"
        mb={6}
        color="gray.600"
        textAlign="center"
      >
        Supported by
      </Text>
      <Center
        m="auto"
        w="100%"
        maxW="container.xl"
        gap={{ base: 10, md: 6, lg: 10, xl: 14 }}
        display="flex"
        flexDirection={{ base: "column", md: "row" }}
        flexWrap="nowrap"
      >
        <Image
          src="/images/carbon13-logo.png"
          alt="Carbon13"
          w={{ base: "50%", md: 120 }}
          minW={0}
        />
        <Image
          src="/images/scottish-edge-logo.png"
          alt="Scottish EDGE"
          w={{ base: "80%", md: 200 }}
          minW={0}
        />
        <Image
          src="/images/geovation-logo.png"
          alt="Geovation by Ordnance Survey"
          w={{ base: "80%", md: 200 }}
          minW={0}
        />
        <Image
          src="/images/os-logo.png"
          alt="Ordnance Survey"
          w={{ base: "60%", md: 150 }}
          minW={0}
        />
        <Image
          src="/images/google-cloud-logo.png"
          alt="Google Cloud for startups"
          w={{ base: "80%", md: 250 }}
          minW={0}
        />
        <Image
          src="/images/sage-impact-logo.png"
          alt="Sage Impact Entrepreneurship Programme 2026"
          w={{ base: "80%", md: 100 }}
          minW={0}
        />
      </Center>
    </Box>

    {/* Value Proposition Section */}
    <Box bg="gray.50">
      <Container maxW="container.xl" py={20}>
        <Heading
          as="h2"
          size="3xl"
          color="brand.700"
          mb={4}
          style={{ textWrap: "balance" } as React.CSSProperties}
        >
          Focus retrofit where it delivers highest value
        </Heading>
        <Text
          maxW="600px"
          color="gray.700"
          fontSize="lg"
          mb={12}
          lineHeight="tall"
        >
          GreenFlip models solar generation, heat loss, and energy use for each
          home in your portfolio, then produces a tailored investment case for
          every retrofit path.
        </Text>

        <Box
          display="grid"
          gridTemplateColumns={{ base: "1fr", md: "2fr 1fr 1fr" }}
          gap={5}
        >
          {/* Featured: 99% stat */}
          <Box bg="brand.700" p={8} borderRadius="xl">
            <Text
              fontSize={{ base: "6xl", md: "8xl" }}
              fontWeight={800}
              lineHeight={1}
              color="brand.500"
              mb={3}
            >
              99%
            </Text>
            <Text
              fontSize="sm"
              fontWeight="medium"
              color="whiteAlpha.700"
              mb={6}
            >
              Reduction in assessment time vs physical survey
            </Text>
            <Text color="white" fontSize="md" lineHeight="tall">
              A portfolio analysis that takes a surveying team weeks runs in
              minutes. Heat loss, solar potential, upgrade costs, and projected
              ROI, for each home, with no site visit.
            </Text>
          </Box>

          {/* Financial case */}
          <Box
            bg="white"
            p={6}
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.200"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Text fontWeight="bold" color="brand.700" fontSize="lg" mb={3}>
              Financial case per property
            </Text>
            <Text color="gray.700" fontSize="sm" lineHeight="tall">
              Every analysis produces a specific outcome: upgrade cost, annual
              savings, ROI, and payback period. Not an energy rating: a
              decision-ready investment case.
            </Text>
          </Box>

          {/* Scale */}
          <Box
            bg="white"
            p={6}
            borderRadius="xl"
            border="1px solid"
            borderColor="gray.200"
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Text fontWeight="bold" color="brand.700" fontSize="lg" mb={3}>
              One home or 10,000
            </Text>
            <Text color="gray.700" fontSize="sm" lineHeight="tall">
              Screen a single acquisition or triage an entire portfolio. The
              analysis scales to any size with the same per-property precision
              and no marginal cost.
            </Text>
          </Box>
        </Box>
      </Container>
    </Box>

    {/* How it works */}
    <HowItWorks />

    {/* Common questions */}
    <FAQ />

    {/* Call to Action */}
    <CallToAction />

    {/* Footer */}
    <Footer />
  </Box>
)

export default LandingPage
